import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  ViewCarouselRounded,
  CloudUploadRounded,
  DeleteOutlineRounded,
  VisibilityRounded,
  ArrowUpwardRounded,
  ArrowDownwardRounded,
  RefreshRounded,
  LayersRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, EmptyState, PanelLoader, StatusPill } from '../components/ui/GlassPanel';
import { useToast } from '../components/ui/ToastProvider';

const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function SignageManager() {
  const toast = useToast();
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [form, setForm] = useState({
    file: null,
    previewUrl: null,
    target_devices: [],
    display_order: 1,
    duration_seconds: 10,
    valid_from: '',
    valid_until: '',
  });

  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/signage/images', {
        params: selectedDevice !== 'all' ? { device_id: selectedDevice } : {},
      });
      setImages(response.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to load the signage library.'));
    } finally {
      setLoading(false);
    }
  }, [selectedDevice, toast]);

  const loadDevices = useCallback(async () => {
    try {
      const response = await api.get('/signage/devices');
      setDevices(response.data || []);
    } catch (error) {
      /* device list is optional context */
    }
  }, []);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const selectFile = (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.warning('Only JPG, PNG and WebP assets are accepted.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.warning('Assets are limited to 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => setForm((current) => ({ ...current, file, previewUrl: event.target.result }));
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!form.file) {
      toast.warning('Select an asset to distribute.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);

    const payload = new FormData();
    payload.append('file', form.file);
    payload.append('target_devices', form.target_devices.length ? form.target_devices.join(',') : 'all');
    payload.append('display_order', form.display_order);
    payload.append('duration_seconds', form.duration_seconds);
    if (form.valid_from) payload.append('valid_from', form.valid_from);
    if (form.valid_until) payload.append('valid_until', form.valid_until);

    try {
      await api.post('/signage/upload', payload, {
        onUploadProgress: (event) => {
          if (event.total) setUploadProgress(Math.round((event.loaded * 100) / event.total));
        },
      });
      toast.success('Asset distributed to the signage endpoints.');
      setUploadOpen(false);
      setForm({ file: null, previewUrl: null, target_devices: [], display_order: 1, duration_seconds: 10, valid_from: '', valid_until: '' });
      loadImages();
    } catch (error) {
      toast.error(errorMessage(error, 'The asset could not be uploaded.'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/signage/${deleteTarget.image_uuid}`, {
        data: { target_devices: deleteTarget.target_devices || [] },
      });
      toast.success('Asset removed from the signage library.');
      setDeleteTarget(null);
      loadImages();
    } catch (error) {
      toast.error(errorMessage(error, 'The asset could not be removed.'));
    }
  };

  const changeOrder = async (image, delta) => {
    const nextOrder = Math.max(1, (image.display_order || 1) + delta);
    try {
      await api.patch(`/signage/${image.image_uuid}/order`, { display_order: nextOrder });
      loadImages();
    } catch (error) {
      toast.error(errorMessage(error, 'The playback order could not be updated.'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Signage Media Library"
          subtitle="Distribute and sequence content for digital signage endpoints"
          icon={<ViewCarouselRounded sx={{ fontSize: 18 }} />}
          action={
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 210 }}>
                <InputLabel>Target endpoint</InputLabel>
                <Select
                  label="Target endpoint"
                  value={selectedDevice}
                  onChange={(event) => setSelectedDevice(event.target.value)}
                >
                  <MenuItem value="all">All endpoints</MenuItem>
                  {devices.map((device) => (
                    <MenuItem key={device.device_id} value={device.device_id}>
                      {device.device_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshRounded sx={{ fontSize: 15 }} />}
                onClick={loadImages}
              >
                Refresh
              </Button>
              <Button variant="contained" startIcon={<CloudUploadRounded sx={{ fontSize: 16 }} />} onClick={() => setUploadOpen(true)}>
                Upload asset
              </Button>
            </Box>
          }
        />

        <Box sx={{ mt: 2.25, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatusPill label={`${images.length} assets`} tone="info" pulse={false} />
          <StatusPill label={`${devices.length} signage endpoints`} tone="secondary" pulse={false} />
        </Box>
      </GlassPanel>

      {loading ? (
        <GlassPanel>
          <PanelLoader label="Loading signage library" />
        </GlassPanel>
      ) : images.length === 0 ? (
        <GlassPanel>
          <EmptyState
            icon={<LayersRounded />}
            title="No signage assets"
            description="Upload imagery to distribute across the signage endpoints."
            minHeight={280}
            action={
              <Button variant="contained" startIcon={<CloudUploadRounded />} onClick={() => setUploadOpen(true)}>
                Upload asset
              </Button>
            }
          />
        </GlassPanel>
      ) : (
        <Grid container spacing={2.5}>
          {images.map((image) => (
            <Grid item xs={12} sm={6} lg={4} key={image.image_uuid}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(palette.secondary, 0.18)}`,
                  background: `linear-gradient(160deg, ${alpha('#0C1428', 0.85)}, ${alpha('#050816', 0.9)})`,
                  transition: 'transform 320ms cubic-bezier(0.22,0.9,0.28,1), border-color 260ms ease',
                  '&:hover': { transform: 'translateY(-4px)', borderColor: alpha(palette.secondary, 0.45) },
                }}
              >
                <Box sx={{ position: 'relative', aspectRatio: '16 / 10', overflow: 'hidden' }}>
                  <Box
                    component="img"
                    src={image.download_url}
                    alt={image.original_name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: `linear-gradient(180deg, transparent 45%, ${alpha('#050816', 0.9)})`,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`#${image.display_order}`}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      fontSize: '0.64rem',
                      color: palette.secondary,
                      background: alpha('#050816', 0.8),
                      border: `1px solid ${alpha(palette.secondary, 0.35)}`,
                    }}
                  />
                  <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Move up">
                      <IconButton
                        size="small"
                        onClick={() => changeOrder(image, -1)}
                        sx={{ color: 'text.primary', background: alpha('#050816', 0.78) }}
                      >
                        <ArrowUpwardRounded sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <IconButton
                        size="small"
                        onClick={() => changeOrder(image, 1)}
                        sx={{ color: 'text.primary', background: alpha('#050816', 0.78) }}
                      >
                        <ArrowDownwardRounded sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, noWrap: 'ellipsis', overflow: 'hidden' }}>
                    {image.original_name}
                  </Typography>
                  <Typography className="mono" sx={{ fontSize: '0.66rem', color: 'text.secondary', mt: 0.3 }}>
                    {formatFileSize(image.file_size)} · {image.duration_seconds}s dwell · {formatDateTime(image.created_at)}
                  </Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityRounded sx={{ fontSize: 14 }} />}
                      onClick={() => setPreview(image)}
                    >
                      Preview
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Delete asset">
                      <IconButton size="small" onClick={() => setDeleteTarget(image)} sx={{ color: palette.danger }}>
                        <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* upload dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload signage asset</DialogTitle>
        <DialogContent>
          <Box
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              selectFile(event.dataTransfer.files[0]);
            }}
            sx={{
              mt: 0.5,
              p: 2.5,
              borderRadius: 3,
              textAlign: 'center',
              cursor: 'pointer',
              border: `1px dashed ${alpha(palette.secondary, 0.35)}`,
              background: alpha(palette.secondary, 0.05),
            }}
            onClick={() => document.getElementById('signage-file-input')?.click()}
          >
            <input
              id="signage-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(event) => selectFile(event.target.files[0])}
            />
            {form.previewUrl ? (
              <Box
                component="img"
                src={form.previewUrl}
                alt="preview"
                sx={{ maxHeight: 180, borderRadius: 2, boxShadow: `0 18px 44px -20px ${alpha(palette.secondary, 0.9)}` }}
              />
            ) : (
              <>
                <CloudUploadRounded sx={{ fontSize: 34, color: palette.secondary, mb: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Drop an asset or click to browse</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.4 }}>
                  JPG, PNG or WebP · maximum 10MB
                </Typography>
              </>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Target endpoints</InputLabel>
                <Select
                  multiple
                  label="Target endpoints"
                  value={form.target_devices}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      target_devices: event.target.value.length ? event.target.value : ['all'],
                    })
                  }
                  renderValue={(selected) =>
                    selected.includes('all') ? 'All endpoints' : `${selected.length} selected`
                  }
                >
                  <MenuItem value="all">All endpoints</MenuItem>
                  {devices.map((device) => (
                    <MenuItem key={device.device_id} value={device.device_id}>
                      {device.device_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Order"
                value={form.display_order}
                onChange={(event) => setForm({ ...form, display_order: event.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Dwell (s)"
                value={form.duration_seconds}
                onChange={(event) => setForm({ ...form, duration_seconds: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Valid from"
                value={form.valid_from}
                onChange={(event) => setForm({ ...form, valid_from: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Valid until"
                value={form.valid_until}
                onChange={(event) => setForm({ ...form, valid_until: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {uploading && <LinearProgress sx={{ mt: 2 }} variant="determinate" value={uploadProgress} />}
        </DialogContent>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setUploadOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={upload} disabled={uploading || !form.file}>
            {uploading ? `Uploading ${uploadProgress}%` : 'Distribute asset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove asset</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.6 }}>
            Delete <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{deleteTarget?.original_name}</Box> and
            withdraw it from every signage endpoint?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={remove}>
            Delete asset
          </Button>
        </DialogActions>
      </Dialog>

      {/* preview */}
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{preview?.original_name}</DialogTitle>
        <DialogContent>
          {preview && (
            <Box
              component="img"
              src={preview.download_url}
              alt={preview.original_name}
              sx={{ width: '100%', borderRadius: 3, border: `1px solid ${alpha(palette.secondary, 0.25)}` }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setPreview(null)} sx={{ color: 'text.secondary' }}>
            Close
          </Button>
          {preview && (
            <Button
              variant="contained"
              onClick={() => {
                const target = devices.find((device) => device.device_id === selectedDevice);
                if (target) navigate(`/devices/${target.device_id}`);
                setPreview(null);
              }}
            >
              Open endpoint
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
